import { TimeFormatType } from '../../../inputPatterns/time/formats';
import {
  FALLBACK_TIME_FORMAT_KEYS_BY_LOCALE,
  SUPPORTED_TIME_FORMATS_BY_LOCALE,
} from '../../../constants/timeConstants';
import { MERIDIEM_REGEX } from '../../../inputPatterns/time/constants';
import { ExtractedMatch } from '../../../types';
import { parseUnitInRange } from '../../../utils/internal/parseUnitInRange';

export type Meridiem = 'AM' | 'PM' | 'am' | 'pm';

export const isMeridiem = (value?: string): value is Meridiem => {
  if (!value) {
    return false;
  }
  const lower = value.toLowerCase();
  return lower === 'am' || lower === 'pm';
};

export const convertTo24Hours = (hourStr: string, meridiem: Meridiem): number | undefined => {
  const hour = parseUnitInRange(hourStr, 1, 12);

  if (!hour) {
    return undefined; // Invalid hour
  }
  const isPM = meridiem.toUpperCase() === 'PM';

  if (hour === 12) {
    return isPM ? 12 : 0; // 12 AM is 0 hours, 12 PM is 12 hours
  }
  return isPM ? hour + 12 : hour; // PM hours are 12 + hour
};

export const get24Hours = (value: string, meridiem?: string): number | undefined => {
  if (meridiem) {
    return undefined; // meridiem should not be present with 24-hour format
  }
  return parseUnitInRange(value, 0, 23);
};

export const getSupportedTimeFormatsForLocale = (locale: string): TimeFormatType[] => {
  const supportedFormats = SUPPORTED_TIME_FORMATS_BY_LOCALE[locale] || SUPPORTED_TIME_FORMATS_BY_LOCALE['en-US'];
  const fallbackFormats = FALLBACK_TIME_FORMAT_KEYS_BY_LOCALE[locale];

  return [...supportedFormats, ...(fallbackFormats || [])];
};
export const getHours = ({
  hour12,
  hour24,
  meridiem,
}: {
  hour12?: string;
  hour24?: string;
  meridiem?: string;
}): number | undefined => {
  if (hour12 && isMeridiem(meridiem)) {
    return convertTo24Hours(hour12, meridiem);
  }
  if (hour24) {
    return get24Hours(hour24);
  }
  return undefined; // input does not contain valid 12 hour or 24 hour format
};

export const getMatchedTimeFormat = (value: string, supportedFormats: TimeFormatType[]) => {
  const valueHasMeridiem = MERIDIEM_REGEX.test(value.trim());
  return Object.values(supportedFormats)
    .map(format => {
      const { regex, expectsMeridiem } = format;

      // Skip formats that are incompatible with meridiem presence
      if (valueHasMeridiem && !expectsMeridiem) return null;
      if (!valueHasMeridiem && expectsMeridiem) return null;

      const match = regex.exec(value);
      if (!match) return null;

      return {
        match: match[0].trim(),
        matchedGroups: { ...match.groups }, // spread to avoid non-enumerable properties from RegExp
        label: format.label,
      };
    })
    .filter((res): res is ExtractedMatch => res !== null)
    .sort((a, b) => b.match.length - a.match.length)[0];
};
